// "How many rows": the preset Top N choices, All, and a number of the user's own. The typed
// number takes digits only and is checked against what there is: asking for more rows than
// the table holds (60 of 55) becomes All. Value 0 = All.
import React, { useEffect, useState } from 'react';

const DEFAULT_PRESETS = [10, 25, 50, 100];

/** The number a typed text asks for: digits only, 0 = All once it reaches `max`. */
export function clampTopN(text, { max = null, allowAll = true, hardMax = null } = {}) {
  const n = parseInt(String(text).replace(/\D/g, ''), 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (max != null && n >= max) return allowAll ? 0 : max;
  if (hardMax != null && n > hardMax) return allowAll ? 0 : hardMax;
  return n;
}

export default function TopNSelect({ id, value, onChange, max = null, presets = DEFAULT_PRESETS, allowAll = true,
  hardMax = null, className = 'ov-select', extra = null }) {
  // extra: [[value, label], ...] choices before the numbers (the PDF's "As on screen"); max: rows available
  const shownPresets = presets.filter((n) => max == null || n < max);
  const isPreset = value === 0 || shownPresets.includes(value) || (extra || []).some(([v]) => v === value);
  const [custom, setCustom] = useState(!isPreset);
  const [text, setText] = useState(isPreset ? '' : String(value));
  useEffect(() => { if (!isPreset) { setCustom(true); setText(String(value)); } }, [value, isPreset]);

  const commit = () => {
    const n = clampTopN(text, { max, allowAll, hardMax });
    if (n === null) { setText(isPreset ? '' : String(value)); return; }
    onChange(n);
    if (n === 0 || shownPresets.includes(n)) { setCustom(false); setText(''); } else setText(String(n));
  };
  const selectValue = custom ? 'custom' : String(value);
  return (
    <span className="so-topn">
      <select id={id} className={className} value={selectValue}
        onChange={(e) => {
          const v = e.target.value;
          if (v === 'custom') { setCustom(true); setText(''); return; }
          setCustom(false);
          const x = (extra || []).find(([ev]) => String(ev) === v);
          onChange(x ? x[0] : Number(v));
        }}>
        {(extra || []).map(([v, label]) => <option key={String(v)} value={String(v)}>{label}</option>)}
        {shownPresets.map((n) => <option key={n} value={String(n)}>Top {n}</option>)}
        {allowAll && <option value="0">All{max != null ? ` (${max})` : ''}</option>}
        <option value="custom">Custom…</option>
      </select>
      {custom && (
        <input type="text" inputMode="numeric" pattern="[0-9]*" className="so-topn-input" aria-label="Number of rows"
          placeholder={max != null ? `1–${max}` : 'rows'} value={text} autoFocus={!text}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            else if (e.key.length === 1 && !/[0-9]/.test(e.key) && !e.ctrlKey && !e.metaKey) e.preventDefault();
          }}
          onChange={(e) => setText(e.target.value.replace(/\D/g, '').slice(0, 5))}
          onBlur={commit} />
      )}
    </span>
  );
}
